import { handleCartModifications } from 'connectors/cart/Cart';
import { getCookies } from 'cookies-next';
import { CartInfoQueryApi, CartInfoQueryVariablesApi, CartQueryDocumentApi } from 'graphql/generated';
import { getUrlWithoutGetParameters } from 'helpers/parsing/urlParsing';
import { useChangePaymentMutation } from 'hooks/cart/useChangePaymentInCart';
import { useCurrentCart } from 'hooks/cart/useCurrentCart';
import { useBroadcastChannel } from 'hooks/useBroadcastChannel';
import { useCurrentUserData } from 'hooks/user/useCurrentUserData';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { usePersistStore } from 'store/usePersistStore';
import { useSessionStore } from 'store/useSessionStore';
import { useClient } from 'urql';

export const useReloadCart = (): void => {
    const { modifications, refetchCart } = useCurrentCart(false);
    const { t } = useTranslation();
    const router = useRouter();
    const slug = getUrlWithoutGetParameters(router.asPath);
    const { isUserLoggedIn } = useCurrentUserData();
    const [changePaymentInCart] = useChangePaymentMutation();
    const updateLastVisitedSlug = useSessionStore((store) => store.updateLastVisitedSlug);
    const lastVisitedSlug = useSessionStore((store) => store.lastVisitedSlug);
    const client = useClient();
    const { cartUuid, defaultStoreUuid, selectedStoreUuid } = usePersistStore((s) => s);

    useBroadcastChannel('refetchCart', () => {
        const cookies = getCookies();
        const isWithUserTokens = !!(cookies.accessToken && cookies.refreshToken);

        if ((isUserLoggedIn && !isWithUserTokens) || (!isUserLoggedIn && isWithUserTokens)) {
            router.reload();
        } else if (cartUuid || isUserLoggedIn) {
            refetchCart();
        }
    });

    useEffect(() => {
        const hasSlugChanged = lastVisitedSlug !== slug;
        updateLastVisitedSlug(slug);

        if ((cartUuid || isUserLoggedIn) && hasSlugChanged) {
            client
                .query<
                    CartInfoQueryApi,
                    CartInfoQueryVariablesApi
                >(CartQueryDocumentApi, { cartUuid, defaultStoreUuid, selectedStoreUuid }, { requestPolicy: 'network-only' })
                .toPromise();
        }
    }, [slug]);

    useEffect(() => {
        if (modifications !== null) {
            handleCartModifications(modifications, t, changePaymentInCart);
        }
    }, [changePaymentInCart, modifications, t]);
};
